import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Compass, Clock, DollarSign } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter
} from '../components/ui/dialog';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { getExcursions, createExcursion, updateExcursion, deleteExcursion } from '../lib/api';
import ImageUploader from '../components/ImageUploader';

const emptyExcursion = {
  name: '',
  name_es: '',
  description: '',
  description_es: '',
  price: 0,
  image_url: '',
  images: [],
  duration: '',
  active: true
};

export default function AdminExcursions() {
  const [excursions, setExcursions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingExcursion, setEditingExcursion] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [formData, setFormData] = useState(emptyExcursion);

  const fetchExcursions = async () => {
    try {
      const response = await getExcursions();
      setExcursions(response.data);
    } catch (error) {
      toast.error('Error loading excursions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExcursions();
  }, []);

  const openCreate = () => {
    setFormData(emptyExcursion);
    setEditingExcursion('new');
  };

  const openEdit = (excursion) => {
    setFormData(excursion);
    setEditingExcursion(excursion.id);
  };

  const handleSave = async () => {
    try {
      if (editingExcursion === 'new') {
        await createExcursion(formData);
        toast.success('Excursion created');
      } else {
        await updateExcursion(editingExcursion, formData);
        toast.success('Excursion updated');
      }
      setEditingExcursion(null);
      fetchExcursions();
    } catch (error) {
      toast.error('Error saving excursion');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteExcursion(deleteId);
      toast.success('Excursion deleted');
      setDeleteId(null);
      fetchExcursions();
    } catch (error) {
      toast.error('Error deleting excursion');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-slate-400">Loading excursions...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-excursions">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-white lg:hidden">Excursions</h1>
        <Button 
          onClick={openCreate}
          className="bg-gradient-to-r from-orange-600 to-yellow-500 text-black font-bold"
          data-testid="add-excursion-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Excursion
        </Button>
      </div>

      {/* Excursions Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {excursions.map((excursion) => (
          <div 
            key={excursion.id} 
            className={`admin-card overflow-hidden ${!excursion.active && 'opacity-60'}`}
            data-testid={`excursion-card-${excursion.id}`}
          >
            <div className="h-40 relative">
              <img 
                src={excursion.image_url} 
                alt={excursion.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1E293B] to-transparent" />
              <div className="absolute top-3 right-3">
                <span className="bg-gradient-to-r from-orange-600 to-yellow-500 text-black font-bold px-3 py-1 rounded-full text-sm">
                  ${excursion.price}
                </span>
              </div>
            </div>
            <div className="p-4">
              <div className="flex justify-between items-start mb-2">
                <h3 className="font-bold text-white">{excursion.name}</h3>
                <span className={`text-xs px-2 py-1 rounded-full ${
                  excursion.active 
                    ? 'bg-green-500/20 text-green-400' 
                    : 'bg-red-500/20 text-red-400'
                }`}>
                  {excursion.active ? 'Active' : 'Inactive'}
                </span>
              </div>
              {excursion.duration && (
                <p className="text-sm text-slate-500 flex items-center gap-1 mb-2">
                  <Clock className="w-4 h-4" />
                  {excursion.duration}
                </p>
              )}
              <p className="text-sm text-slate-400 line-clamp-2 mb-4">{excursion.description}</p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="flex-1 border-white/10 text-white hover:bg-white/5"
                  onClick={() => openEdit(excursion)}
                >
                  <Pencil className="w-4 h-4 mr-2" />
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                  onClick={() => setDeleteId(excursion.id)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {excursions.length === 0 && (
        <div className="admin-card p-12 text-center">
          <Compass className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-400">No excursions found</p>
        </div>
      )}

      {/* Edit/Create Dialog */}
      <Dialog open={!!editingExcursion} onOpenChange={() => setEditingExcursion(null)}>
        <DialogContent className="bg-[#1E293B] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingExcursion === 'new' ? 'Add New Excursion' : 'Edit Excursion'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm text-slate-400">Name (EN)</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="bg-white/5 border-white/10 text-white"
                  placeholder="Isla Saona"
                  data-testid="excursion-name-input"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm text-slate-400">Name (ES)</label>
                <Input
                  value={formData.name_es || ''}
                  onChange={(e) => setFormData({ ...formData, name_es: e.target.value })}
                  className="bg-white/5 border-white/10 text-white"
                  placeholder="Isla Saona"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm text-slate-400">Price (USD)</label>
                <Input
                  type="number"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
                  className="bg-white/5 border-white/10 text-white"
                  data-testid="excursion-price-input"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm text-slate-400">Duration</label>
                <Input
                  value={formData.duration || ''}
                  onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                  className="bg-white/5 border-white/10 text-white"
                  placeholder="8 hours"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm text-slate-400">Description (EN)</label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="bg-white/5 border-white/10 text-white"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm text-slate-400">Description (ES)</label>
              <Textarea
                value={formData.description_es || ''}
                onChange={(e) => setFormData({ ...formData, description_es: e.target.value })}
                className="bg-white/5 border-white/10 text-white"
              />
            </div>

            <ImageUploader
              images={formData.images || (formData.image_url ? [formData.image_url] : [])}
              onChange={(imgs) => setFormData({ ...formData, images: imgs, image_url: imgs[0] || '' })}
              maxImages={10}
              label="Fotos de la Excursión (hasta 10)"
            />

            <div className="flex items-center justify-between">
              <label className="text-sm text-slate-400">Active</label>
              <Switch
                checked={formData.active}
                onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditingExcursion(null)}
              className="border-white/10 text-white hover:bg-white/5"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              className="bg-cyan-600 hover:bg-cyan-700"
              data-testid="save-excursion-btn"
            >
              Save Excursion
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-[#1E293B] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Excursion?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
